import React from "react";
import {
  Box,
  Container,
  Paper,
  Skeleton,
  Grid,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import Navbar from "../Page Component/Navbar";
import Footer from "../Page Component/Footer";

const ComplianceFormSkeleton: React.FC = () => {
  // Generate a skeleton table with 5 rows and 5 columns to simulate the compliance form
  const renderTableRows = () => {
    return Array(5)
      .fill(0)
      .map((_, rowIndex) => (
        <TableRow key={`row-${rowIndex}`}>
          <TableCell
            sx={{
              position: "sticky",
              left: 0,
              zIndex: 1,
              width: "240px",
              backgroundColor: rowIndex % 2 === 0 ? "#fafafa" : "#ffffff",
              borderRight: "1px solid #e0e0e0",
            }}
          >
            <Skeleton variant="text" height={40} animation="wave" />
          </TableCell>
          {Array(4)
            .fill(0)
            .map((_, colIndex) => (
              <React.Fragment key={`col-${rowIndex}-${colIndex}`}>
                <TableCell align="center" sx={{ width: "120px" }}>
                  <Skeleton
                    variant="rounded"
                    height={36}
                    width="100%"
                    animation="wave"
                  />
                </TableCell>
                <TableCell
                  sx={{
                    width: "200px",
                    borderRight: colIndex < 3 ? "1px solid #e0e0e0" : "none",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    height={60}
                    width="100%"
                    animation="wave"
                  />
                </TableCell>
              </React.Fragment>
            ))}
        </TableRow>
      ));
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 12, mb: 8 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          {/* Header Section */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Skeleton
              variant="text"
              width="300px"
              height={40}
              animation="wave"
            />
            <Skeleton
              variant="rounded"
              width="120px"
              height={32}
              animation="wave"
            />
          </Box>

          {/* Form Details Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {Array(6)
              .fill(0)
              .map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Skeleton
                    variant="text"
                    width="120px"
                    height={24}
                    animation="wave"
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="180px"
                    height={28}
                    animation="wave"
                  />
                </Grid>
              ))}
          </Grid>

          {/* Department Cards */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <Skeleton
              variant="text"
              width="250px"
              height={32}
              animation="wave"
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              {Array(4)
                .fill(0)
                .map((_, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Box
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        height: "100%",
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Skeleton
                          variant="text"
                          width="120px"
                          height={24}
                          animation="wave"
                        />
                        <Skeleton
                          variant="rounded"
                          width="40px"
                          height={24}
                          animation="wave"
                        />
                      </Box>
                      <Skeleton
                        variant="text"
                        width="80px"
                        height={20}
                        animation="wave"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Grid>
                ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Form Table */}
          <Box sx={{ position: "relative", overflowX: "auto" }}>
            <Table sx={{ tableLayout: "fixed", minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell
                    sx={{
                      width: "240px",
                      position: "sticky",
                      left: 0,
                      backgroundColor: "white",
                      zIndex: 2,
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    <Skeleton variant="text" height={24} animation="wave" />
                  </TableCell>
                  {Array(4)
                    .fill(0)
                    .map((_, index) => (
                      <React.Fragment key={`header-${index}`}>
                        <TableCell align="center" sx={{ width: "120px" }}>
                          <Skeleton
                            variant="text"
                            height={24}
                            animation="wave"
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: "200px",
                            borderRight:
                              index < 3 ? "1px solid #e0e0e0" : "none",
                          }}
                        >
                          <Skeleton
                            variant="text"
                            height={24}
                            animation="wave"
                          />
                        </TableCell>
                      </React.Fragment>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Department Header Row */}
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{
                      backgroundColor: "primary.light",
                      position: "sticky",
                      left: 0,
                      zIndex: 0,
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Skeleton
                        variant="text"
                        width="200px"
                        height={28}
                        animation="wave"
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
                      />
                      <Skeleton
                        variant="rounded"
                        width="40px"
                        height={24}
                        animation="wave"
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
                {renderTableRows()}

                {/* Second Department Header Row */}
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{
                      backgroundColor: "primary.light",
                      position: "sticky",
                      left: 0,
                      zIndex: 0,
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Skeleton
                        variant="text"
                        width="200px"
                        height={28}
                        animation="wave"
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
                      />
                      <Skeleton
                        variant="rounded"
                        width="40px"
                        height={24}
                        animation="wave"
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
                {renderTableRows().slice(0, 3)}
              </TableBody>
            </Table>
          </Box>

          {/* Button Section */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Skeleton
              variant="rounded"
              width="100px"
              height={36}
              animation="wave"
              sx={{ mr: 2 }}
            />
            <Skeleton
              variant="rounded"
              width="100px"
              height={36}
              animation="wave"
            />
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default ComplianceFormSkeleton;
